// src/docs/DocsBuyTokenizedProject.js
import React from "react";
import { Container, Typography } from "@mui/material";

const DocsBuyTokenizedProject = () => {
  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      {/* Page Title */}
      <Typography variant="h5" sx={{ color: "#FFD700", mb: 3 }}>
        Buying a Tokenized Project
      </Typography>

      {/* Introduction */}
      <Typography variant="body1" sx={{ color: "#FFFFFF", mb: 3 }}>
        Tokenized projects allow you to invest in innovative ideas and ventures by purchasing digital shares that represent a fraction of the project’s equity or assets. This guide explains how you can purchase these shares on CrowdLaunch, detailing every step of the process, the fee structure, and important security considerations.
      </Typography>

      {/* What Is a Tokenized Project? */}
      <Typography variant="h6" sx={{ color: "#FFD700", mb: 1 }}>
        What Is a Tokenized Project?
      </Typography>
      <Typography variant="body2" sx={{ color: "#FFFFFF", mb: 3 }}>
        A tokenized project converts a traditional project into a digital asset on the blockchain. In this system, ownership is represented by token shares, which can be bought, sold, or used later for staking. Tokenization opens the door for fractional ownership and easier liquidity, enabling investors to access opportunities that would otherwise require significant capital.
      </Typography>

      {/* The Buying Process */}
      <Typography variant="h6" sx={{ color: "#FFD700", mb: 1 }}>
        The Buying Process
      </Typography>
      <Typography variant="body2" sx={{ color: "#FFFFFF", mb: 3 }}>
        The process of purchasing a tokenized project on CrowdLaunch is designed to be simple and transparent:
        <br /><br />
        1. <strong>Connect Your Wallet:</strong> Ensure your injected crypto wallet (such as MetaMask) is connected and set to Smart Chain.
        <br />
        2. <strong>Select a Project:</strong> Browse through tokenized projects and choose one that aligns with your investment goals. Detailed project information, including share price and funding status, is provided along with external links provided from the project tokenizor.
        <br />
        3. <strong>Initiate Purchase:</strong> Click the “Buy” button on the project’s page, then enter the amount of BNB you wish to put in.
        <br />
        4. <strong>Confirm the Transaction:</strong> Review the transaction details in your wallet interface and confirm. The smart contract calculates how many tokens you will receive based on the fixed inital share price.
        <br />
        5. <strong>Share Transfer:</strong> Upon confirmation, the token shares are transferred directly to your wallet and the funds are secured in the project’s contract.
      </Typography>

      {/* Fixed Rate & Fee Structure */}
      <Typography variant="h6" sx={{ color: "#FFD700", mb: 1 }}>
        Fixed Rate &amp; Fee Structure
      </Typography>
      <Typography variant="body2" sx={{ color: "#FFFFFF", mb: 3 }}>
        For now, CrowdLaunch will use a fixed rate for share purchases during the initial sale. This fixed rate offers several benefits:
        <br /><br />
        • <strong>Price Predictability:</strong> You know the exact price at which you are buying shares.
        <br />
        • <strong>Stable Investment Environment:</strong> A fixed rate protects early investors from market volatility during the sale.
        <br />
        Additionally, several fees apply:
        <br /><br />
        • <strong>Tokenization Fee:</strong> A fee for converting your project into a tokenized asset.
        <br />
        • <strong>Transaction Fee:</strong> Covers the cost of executing transactions on the blockchain and is paid in BNB.
        <br />
        • <strong>Staking Fee:</strong> A portion of the fee collected is given back to stakers through staking pool implementation.
      </Typography>

      {/* Security Considerations */}
      <Typography variant="h6" sx={{ color: "#FFD700", mb: 1 }}>
        Security Considerations
      </Typography>
      <Typography variant="body2" sx={{ color: "#FFFFFF", mb: 3 }}>
        Security is paramount when buying tokenized projects. Consider the following:
        <br /><br />
        • <strong>Verify the Platform:</strong> Only connect your wallet through the official CrowdLaunch website.
        <br />
        • <strong>Network Confirmation:</strong> Ensure your wallet is on the correct blockchain network to avoid general bugs and sending funds to the wrong contract.
        <br />
        • <strong>Review Transaction Details:</strong> Always double-check the transaction summary in your wallet before confirming.
      </Typography>

      {/* Post-Purchase Actions */}
      <Typography variant="h6" sx={{ color: "#FFD700", mb: 1 }}>
        What Happens After Your Purchase?
      </Typography>
      <Typography variant="body2" sx={{ color: "#FFFFFF", mb: 3 }}>
        After purchasing shares, they will be credited directly to your wallet. Once you own these shares, you can:
        <br /><br />
        • Hold them as an investment in anticipation of project growth.
        <br />
        • Trade them on secondary market for for your chosen price based on demand.
        <br /><br />
        Monitor your investment via your CrowdLaunch portfolio, and stay updated with project developments to maximize your potential returns.
      </Typography>

      {/* Conclusion */}
      <Typography variant="body1" sx={{ color: "#FFFFFF", mt: 4 }}>
        Buying a tokenized project on CrowdLaunch is a straightforward process that offers a secure and predictable entry point into innovative ventures. By understanding the fixed rate, fee structure, and the overall process, you can confidently invest in projects that align with your goals.
      </Typography>
    </Container>
  );
};

export default DocsBuyTokenizedProject;
